import * as React from 'react'
import { Loading01Icon } from '@repo/studio-design-system-ui-icons/general/loading-01'
import { tw } from 'utils/classnames'
import type { MascotMove as MascotMoveName } from '../motion/move-catalog'
import type { MascotFacing } from '../render/types'
import type { MascotHandle } from './mascot-handle'
import { useMascotController } from './use-mascot-controller'

const MascotRenderer = React.lazy(() =>
  import('../render/renderer').then(module => ({ default: module.MascotRenderer })),
)

export type MascotMove = MascotMoveName

export function Mascot({
  modelUrl,
  facing = 'left',
  onActivate,
  placeholderSrc,
  active = true,
  label = 'Mascot',
  className,
  ref,
}: {
  modelUrl: string
  facing?: MascotFacing
  onActivate?: () => void
  placeholderSrc?: string
  active?: boolean
  label?: string
  className?: string
  ref?: React.Ref<MascotHandle>
}) {
  const { wakeToken, ready, motion, moves, requestRef, onFailure, onSession, handleClick } =
    useMascotController({ active, ref })

  return (
    <button
      type="button"
      className={tw('relative size-32 overflow-hidden', className)}
      aria-label={label}
      aria-busy={!ready}
      onClick={onActivate ?? handleClick}
    >
      <React.Suspense fallback={null}>
        <MascotRenderer
          modelUrl={modelUrl}
          motion={motion}
          moves={moves}
          requestRef={requestRef}
          facing={facing}
          wakeToken={wakeToken}
          onFailure={onFailure}
          onSession={onSession}
        />
      </React.Suspense>
      <MascotStatusOverlay placeholderSrc={placeholderSrc} ready={ready} />
    </button>
  )
}

function MascotStatusOverlay({
  placeholderSrc,
  ready,
}: {
  placeholderSrc?: string
  ready: boolean
}) {
  if (placeholderSrc) {
    return <MascotPlaceholder src={placeholderSrc} fading={ready} />
  }
  if (!ready) {
    return <MascotSpinner />
  }
  return null
}

function MascotPlaceholder({ src, fading }: { src: string; fading: boolean }) {
  const [present, setPresent] = React.useState(true)
  return present ? (
    <img
      src={src}
      alt=""
      className={tw(
        'pointer-events-none absolute inset-0 z-10 -ml-1 size-full origin-center scale-90 object-contain transition-opacity duration-300',
        fading && 'opacity-0',
      )}
      onTransitionEnd={event => {
        if (event.propertyName === 'opacity') {
          setPresent(false)
        }
      }}
    />
  ) : null
}

function MascotSpinner() {
  return (
    <output
      aria-live="polite"
      className="absolute inset-0 z-10 grid place-items-center rounded-2 bg-gray-700 text-gray-400"
    >
      <Loading01Icon className="size-4 animate-spin" />
    </output>
  )
}
